/**
 * The one-off Contact Picker, above a phone field. **Progressive enhancement over a manual field
 * that is always present** — on every browser without `navigator.contacts` (which is most of them,
 * iOS Safari included) this renders nothing at all and the caller's own input is the whole flow.
 *
 * Written for `invite` (§7.7) and now also the install flow's owner step (§7.8), which asks for the
 * same thing — a phone number for someone already in the installer's address book — and had only
 * the manual field. **Shared rather than copied**, because the two would have to agree on the
 * privacy rationale, the cancel/unavailable/failed branches, and the multi-number list, and two
 * copies of that agree only until one is edited. The copy moved to its own `contacts` namespace for
 * the same reason: it now belongs to neither screen.
 *
 * **Nothing here reads, stores, or asks for an address-book permission.** The OS picker hands back
 * exactly the contact the user chose, this component keeps the phone numbers only long enough for
 * them to pick one, and nothing it sees is cached or sent anywhere except the number the caller
 * puts in its field. That is why the rationale line sits above the button rather than in a consent
 * dialog: there is no standing grant to explain.
 *
 * **Availability is discovered on mount, not at the tap.** `getProperties()` is async, and the
 * `select()` that follows must stay inside the click's own user activation — a browser drops the
 * picker if it is opened from a promise resolved later. So the button only exists once the
 * properties are known, and `chooseContact` opens the picker straight from the handler.
 */
import { useEffect, useRef, useState } from 'react'
import type { E164 } from '@selogate/core'
import { Bidi } from '../../components'
import {
  contactPhoneSelections,
  contactPickerProperties,
  pickContacts,
  type ContactPhoneSelection,
  type ContactProperty,
} from '../../device-capabilities/contacts'
import { useTranslation } from '../../i18n/I18nProvider'

export function ContactPickerBlock({
  value,
  onPick,
}: {
  /**
   * What the caller's phone field currently holds. Only read to know when it has moved on: a
   * pending "which of this contact's numbers?" list is stale the moment the field says something
   * else, and leaving it up offers a second answer to a question already answered by hand.
   */
  readonly value: string
  onPick(phone: E164): void
}): JSX.Element | null {
  const { t } = useTranslation('contacts')
  const [properties, setProperties] = useState<readonly ContactProperty[] | undefined>(undefined)
  const [status, setStatus] = useState<'idle' | 'opening' | 'empty' | 'failed'>('idle')
  const [choice, setChoice] = useState<ContactPhoneSelection | undefined>(undefined)
  /** The last number this component handed up, so its own write does not read as a hand edit. */
  const picked = useRef<string | undefined>(undefined)

  useEffect(() => {
    let current = true
    void contactPickerProperties().then((available) => {
      // Only ever an *enabling* write. Answering "no picker" changes nothing — the initial state
      // already says that — and on the browsers that answer no, which is most of them, skipping it
      // keeps a render out of every screen that mounts this.
      if (current && available !== undefined) setProperties(available)
    })
    return () => {
      current = false
    }
  }, [])

  useEffect(() => {
    if (value === picked.current) return
    setChoice(undefined)
    setStatus('idle')
  }, [value])

  async function chooseContact(): Promise<void> {
    if (properties === undefined) return
    setStatus('opening')
    setChoice(undefined)
    const result = await pickContacts({ multiple: false, properties })
    if (result.kind === 'cancelled') {
      setStatus('idle')
      return
    }
    // The capability answered "no" late — withdraw the affordance rather than leave a button that
    // does nothing. The manual field below it never went away.
    if (result.kind === 'unavailable') {
      setProperties(undefined)
      setStatus('idle')
      return
    }
    if (result.kind === 'failed') {
      setStatus('failed')
      return
    }

    // A contact with no number the platform accepts is not a failure of the picker, and saying
    // "contacts could not be opened" about it would send the user back to a picker that worked.
    const selected = contactPhoneSelections(result.contacts)[0]
    if (selected === undefined) {
      setStatus('empty')
      return
    }
    if (selected.phones.length === 1) {
      use(selected.phones[0] as E164)
      return
    }
    setChoice(selected)
    setStatus('idle')
  }

  function use(phone: E164): void {
    picked.current = phone
    setChoice(undefined)
    setStatus('idle')
    onPick(phone)
  }

  // Unsupported and undiscovered look the same on purpose: there is nothing to report about a
  // capability the browser does not have, and the field the caller renders is unaffected either way.
  if (properties === undefined && status === 'idle' && choice === undefined) return null

  return (
    <>
      {properties !== undefined ? (
        <div className="contact-picker-block">
          <p className="hint">{t('contactRationale')}</p>
          <button
            className="btn-secondary"
            type="button"
            disabled={status === 'opening'}
            onClick={() => void chooseContact()}
          >
            {status === 'opening' ? t('contactOpening') : t('contactChoose')}
          </button>
        </div>
      ) : null}
      {choice !== undefined ? (
        <div
          className="contact-phone-choices"
          aria-label={t('contactChooseNumber', { name: choice.name ?? t('contactSelected') })}
        >
          <div className="section-title">
            {t('contactChooseNumber', { name: choice.name ?? t('contactSelected') })}
          </div>
          {choice.phones.map((phone) => (
            <button
              className="row"
              data-interactive="true"
              type="button"
              key={phone}
              onClick={() => use(phone)}
            >
              <span className="l">
                <Bidi>{phone}</Bidi>
              </span>
            </button>
          ))}
        </div>
      ) : null}
      {status === 'empty' ? <p className="hint error">{t('contactNoPhone')}</p> : null}
      {status === 'failed' ? <p className="hint error">{t('contactFailed')}</p> : null}
    </>
  )
}
